/**
 * Social Security benefit math (spec §8) beyond the claiming multiplier:
 *
 *  - PIA from a 35-year earnings history via the bend-point formula.
 *  - Spousal benefits: up to 50% of the other spouse's PIA, reduced for early
 *    claiming by the SPOUSAL schedule (25/36 of 1%/mo for 36 months, then
 *    5/12 of 1%/mo) — and delayed retirement credits do NOT apply to spousal
 *    benefits, a detail calculators routinely get wrong.
 *  - Survivor benefits: up to 100% of the deceased's benefit including any
 *    delayed credits they earned (full at FRA; the age-60 reduced path is
 *    noted but the projection models deaths at/after FRA ages).
 *  - Earnings test: $1 withheld per $2 above the limit before FRA ($1/$3 in
 *    the FRA year), with the FRA RECOMPUTATION that credits withheld months —
 *    modeling only the withholding overstates the cost of working.
 */
import { EARNINGS_TEST_2026 } from './data/socialSecurity';

/**
 * 2026 PIA bend points (monthly, for workers first eligible in 2026) and the
 * 90/32/15 factors. Verified against two secondary sources 2026-08-03;
 * cross-check SSA.gov automatic-adjustment figures before relying on the
 * earnings-history path for a real decision (the SSA-statement path is exact
 * by construction).
 */
export const BEND_POINT_1_2026 = 1_286;
export const BEND_POINT_2_2026 = 7_749;

/**
 * Monthly PIA from an earnings history. Approximation: earnings are taken as
 * already wage-indexed (enter them in today's dollars); AIME = top 35 years
 * / 420 months.
 */
export function piaFromEarnings(earnings: { year: number; earnings: number }[]): number {
  const top35 = earnings
    .map((e) => Math.max(0, e.earnings))
    .sort((a, b) => b - a)
    .slice(0, 35);
  const aime = top35.reduce((t, e) => t + e, 0) / 420;
  return (
    0.9 * Math.min(aime, BEND_POINT_1_2026) +
    0.32 * Math.max(0, Math.min(aime, BEND_POINT_2_2026) - BEND_POINT_1_2026) +
    0.15 * Math.max(0, aime - BEND_POINT_2_2026)
  );
}

/**
 * Spousal reduction multiplier for claiming at `claimAgeMonths` vs. FRA.
 * 25/36 of 1% per month for the first 36 months early, 5/12 of 1% beyond.
 * NEVER exceeds 1 — delayed credits do not apply to spousal benefits.
 */
export function spousalMultiplier(claimAgeMonths: number, fraMonths: number): number {
  if (claimAgeMonths >= fraMonths) return 1;
  const monthsEarly = fraMonths - claimAgeMonths;
  const first36 = Math.min(monthsEarly, 36);
  const beyond = Math.max(0, monthsEarly - 36);
  return 1 - (first36 * (25 / 36 / 100) + beyond * (5 / 12 / 100));
}

export interface EarningsTestResult {
  withheld: number;
  /** Months of benefit effectively withheld (credited back at FRA). */
  withheldMonths: number;
}

/**
 * Earnings-test withholding for a year of claiming before FRA while working.
 * @param annualBenefit   the year's benefit before withholding
 * @param earnings        the person's earned income this year (nominal)
 * @param isFraYear       true in the calendar year FRA is reached
 * @param inflationFactor limits are wage-indexed; we scale by inflation
 */
export function earningsTestWithholding(
  annualBenefit: number,
  earnings: number,
  isFraYear: boolean,
  inflationFactor: number,
): EarningsTestResult {
  if (annualBenefit <= 0 || earnings <= 0) return { withheld: 0, withheldMonths: 0 };
  const limit =
    (isFraYear ? EARNINGS_TEST_2026.fraYearAnnual : EARNINGS_TEST_2026.underFraAnnual) *
    inflationFactor;
  const excess = Math.max(0, earnings - limit);
  if (excess === 0) return { withheld: 0, withheldMonths: 0 };
  const rate = isFraYear ? 1 / 3 : 1 / 2;
  const withheld = Math.min(annualBenefit, excess * rate);
  const monthly = annualBenefit / 12;
  return { withheld, withheldMonths: monthly > 0 ? Math.min(12, withheld / monthly) : 0 };
}
